/**
 * HomeSpotlight — Iter 320bo (Feb 2026)
 * Splash modal shown on the public home page with a carousel of up to 5
 * spotlights. Language: honors current LanguageContext (already computed from
 * localStorage → navigator.language → 'pt'). Frequency respected per item.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, MapPin, Clock, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_STYLES = {
  neutral:     { bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', color: '#78350f', accent: '#b45309' },
  urgent:      { bg: 'linear-gradient(135deg,#fee2e2,#fecaca)', color: '#7f1d1d', accent: '#b91c1c' },
  celebration: { bg: 'linear-gradient(135deg,#fefce8,#fde047)', color: '#713f12', accent: '#a16207' },
  spiritual:   { bg: 'linear-gradient(135deg,#f3e8ff,#e9d5ff)', color: '#4c1d95', accent: '#7c3aed' },
  clean_white: { bg: '#ffffff', color: '#0f172a', accent: '#111A2C' },
};

const tagLabels = {
  esgotado:      { pt: 'Esgotado', en: 'Sold Out', es: 'Agotado' },
  ultimas_vagas: { pt: 'Últimas Vagas', en: 'Last Spots', es: 'Últimas Plazas' },
  em_breve:      { pt: 'Em Breve', en: 'Coming Soon', es: 'Próximamente' },
};
const tagColors = { esgotado: '#dc2626', ultimas_vagas: '#f97316', em_breve: '#16a34a' };

// Check if a given spotlight should be shown to THIS visitor based on
// frequency + storage flag. Returns true if it can be shown.
const shouldShow = (sp) => {
  const key = `spotlight_seen_${sp.id}`;
  if (sp.frequency === 'always') return true;
  if (sp.frequency === 'daily') {
    const last = localStorage.getItem(key);
    if (!last) return true;
    return new Date(last).toDateString() !== new Date().toDateString();
  }
  // session (default)
  return !sessionStorage.getItem(key);
};

const markShown = (sp) => {
  const key = `spotlight_seen_${sp.id}`;
  if (sp.frequency === 'daily' || sp.frequency === 'always') {
    localStorage.setItem(key, new Date().toISOString());
  } else {
    sessionStorage.setItem(key, '1');
  }
};

const HomeSpotlight = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    // Delay 800ms after mount so the LCP is not blocked
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/spotlight/active?lang=${language}`);
        if (!r.ok) return;
        const list = await r.json();
        const filtered = (list || []).filter(shouldShow);
        if (filtered.length === 0) return;
        setItems(filtered);
        setOpen(true);
      } catch {}
    }, 800);
    return () => clearTimeout(timer);
  }, [language]);

  // Iter 321c — Auto-rotate the featured slide every 6.5s. Pauses while
  // paused=true (hovered thumbnails column).
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!open || items.length < 2 || paused) return;
    const id = setInterval(() => {
      setSelected((prev) => (prev + 1) % items.length);
    }, 6500);
    return () => clearInterval(id);
  }, [open, items.length, paused]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') dismiss(); };
    if (open) window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = useCallback(() => {
    items.forEach(markShown);
    setOpen(false);
  }, [items]);

  const clickCard = (sp) => {
    // gtag analytics (safe no-op if gtag not present)
    try { window.gtag?.('event', 'spotlight_click', { spotlight_id: sp.id, spotlight_type: sp.type }); } catch (_) {}
    // Iter 320bs — server-side click counter (fire-and-forget)
    try { fetch(`${API}/api/spotlight/${sp.id}/click`, { method: 'POST' }); } catch (_) {}
    // Iter 320bt — Attribution: remember which spotlight sent the pilgrim
    // so we can credit any resulting booking to this campaign. 30-day window.
    try {
      sessionStorage.setItem('spotlight_attr', JSON.stringify({
        id: sp.id, type: sp.type, at: Date.now(),
      }));
    } catch (_) {}
    markShown(sp);
    if (sp.type === 'tour' && sp.tour_card) {
      setOpen(false);
      navigate(`/tour/${sp.tour_card.id}`);
    } else if (sp.type === 'external' && sp.external_url) {
      window.open(sp.external_url, '_blank', 'noopener,noreferrer');
    } else if (sp.type === 'internal' && sp.click_url) {
      setOpen(false);
      if (sp.click_url.startsWith('http')) window.open(sp.click_url, '_blank', 'noopener,noreferrer');
      else navigate(sp.click_url);
    }
  };

  if (!open || items.length === 0) return null;

  const featured = items[selected];
  const hasMultiple = items.length > 1;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      onClick={dismiss} data-testid="home-spotlight-modal">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-6xl w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={dismiss} data-testid="spotlight-close"
          className="absolute top-3 right-3 z-30 bg-white/90 hover:bg-white rounded-full p-2 shadow-md">
          <X className="w-5 h-5 text-gray-700"/>
        </button>

        <div className={`flex flex-col ${hasMultiple ? 'md:flex-row' : ''}`}>
          {/* Coluna de thumbnails (só quando há 2+) */}
          {hasMultiple && (
            <aside
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              className="md:w-56 bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-200
                         px-4 py-6 md:py-8 md:max-h-[640px] md:overflow-y-auto"
              data-testid="spotlight-thumbs">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4 hidden md:block">
                {language === 'pt' ? 'Destaques' : language === 'en' ? 'Highlights' : 'Destacados'}
              </div>
              <div className="flex md:flex-col gap-3 overflow-x-auto md:overflow-visible">
                {items.map((sp, idx) => {
                  const active = idx === selected;
                  return (
                    <button key={sp.id}
                      onClick={() => setSelected(idx)}
                      data-testid={`spotlight-thumb-${idx}`}
                      className={`shrink-0 w-40 md:w-full text-left rounded-lg overflow-hidden border-2
                                  transition-all duration-300
                                  ${active ? 'border-demarre-800 shadow-md scale-[1.02]'
                                           : 'border-transparent hover:border-slate-300 opacity-70 hover:opacity-100'}`}>
                      {renderThumb(sp, language)}
                    </button>
                  );
                })}
              </div>
            </aside>
          )}

          {/* Slide em destaque */}
          <div className="flex-1 relative">
            <div key={featured.id} className="animate-[fadeIn_500ms_ease-out]">
              {renderCard(featured, language, clickCard)}
            </div>
            {hasMultiple && (
              <>
                <button
                  onClick={() => setSelected((prev) => (prev - 1 + items.length) % items.length)}
                  data-testid="spotlight-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white rounded-full p-2 shadow-md">
                  <ChevronLeft className="w-5 h-5"/>
                </button>
                <button
                  onClick={() => setSelected((prev) => (prev + 1) % items.length)}
                  data-testid="spotlight-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white rounded-full p-2 shadow-md">
                  <ChevronRight className="w-5 h-5"/>
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                  {items.map((_, i) => (
                    <button key={i} onClick={() => setSelected(i)}
                      className={`w-2 h-2 rounded-full transition ${i === selected ? 'bg-white shadow' : 'bg-white/40'}`}
                      aria-label={`Slide ${i+1}`}/>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0);} }
      `}</style>
    </div>
  );
};

// Thumbnail compacta para a coluna lateral. Reflete o tipo do spotlight.
function renderThumb(sp, language) {
  if (sp.type === 'tour' && sp.tour_card) {
    const t = sp.tour_card;
    const img = t.image?.startsWith('/api') ? `${API}${t.image}` : t.image;
    const name = t[`name_${language}`] || t.name_pt;
    return (
      <>
        <div className="aspect-video overflow-hidden">
          <img src={img} alt={name} className="w-full h-full object-cover"/>
        </div>
        <div className="px-2 py-2 bg-white">
          <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-tight">{name}</p>
        </div>
      </>
    );
  }
  if (sp.type === 'external') {
    const img = sp[`image_url_${language}`] || sp.image_url_pt;
    if (!img) return <div className="aspect-video bg-slate-200 flex items-center justify-center text-[10px] text-slate-500">—</div>;
    const src = img.startsWith('/') ? `${API}${img}` : img;
    return <div className="aspect-video overflow-hidden"><img src={src} className="w-full h-full object-cover" alt=""/></div>;
  }
  if (sp.type === 'internal') {
    const st = TEMPLATE_STYLES[sp.template_key] || TEMPLATE_STYLES.neutral;
    const title = sp[`title_${language}`] || sp.title_pt || '';
    const isClean = sp.template_key === 'clean_white';
    return (
      <div className="aspect-video flex items-center justify-center px-2"
        style={{ background: st.bg, border: isClean ? '1px solid #e5e7eb' : 'none' }}>
        <p className="text-[11px] font-semibold text-center line-clamp-3"
          style={{ color: st.color, fontFamily: 'Fraunces, Georgia, serif' }}>
          {title || '—'}
        </p>
      </div>
    );
  }
  return null;
}

function renderCard(sp, language, onClick) {
  if (sp.type === 'tour' && sp.tour_card) {
    const t = sp.tour_card;
    const img = t.image?.startsWith('/api') ? `${API}${t.image}` : t.image;
    const name = t[`name_${language}`] || t.name_pt;
    const dest = t[`destination_${language}`] || t.destination_pt;
    const tagKey = t.status_tag;
    return (
      <div className="cursor-pointer" onClick={() => onClick(sp)} data-testid={`spotlight-card-tour-${sp.id}`}>
        <div className="relative aspect-video overflow-hidden">
          <img src={img} alt={name} className="w-full h-full object-cover"/>
          {tagKey && tagLabels[tagKey] && (
            <span className="absolute top-4 left-4 text-white font-bold text-xs px-3 py-1 rounded-full shadow-lg"
              style={{ background: tagColors[tagKey] }}>
              {tagLabels[tagKey][language]}
            </span>
          )}
        </div>
        <div className="p-6 bg-white">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{name}</h2>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/> {dest}</span>
            {t.duration && <span className="flex items-center gap-1"><Clock className="w-4 h-4"/> {t.duration} {language === 'pt' ? 'dias' : language === 'en' ? 'days' : 'días'}</span>}
            {t.departure_date && <span className="flex items-center gap-1"><Calendar className="w-4 h-4"/>
              {new Date(t.departure_date + 'T12:00:00').toLocaleDateString(language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: 'short' })}
              {t.arrival_date && ' — ' + new Date(t.arrival_date + 'T12:00:00').toLocaleDateString(language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>}
          </div>
        </div>
      </div>
    );
  }
  if (sp.type === 'external') {
    const img = sp[`image_url_${language}`] || sp.image_url_pt;
    if (!img) return <div className="p-12 text-center text-gray-400">Sem arte disponível</div>;
    const src = img.startsWith('/') ? `${API}${img}` : img;
    return (
      <div className="cursor-pointer" onClick={() => onClick(sp)} data-testid={`spotlight-card-external-${sp.id}`}
        style={{ aspectRatio: '16/9' }}>
        <img src={src} alt="destaque" className="w-full h-full object-cover"/>
      </div>
    );
  }
  if (sp.type === 'internal') {
    const st = TEMPLATE_STYLES[sp.template_key] || TEMPLATE_STYLES.neutral;
    const title = sp[`title_${language}`] || sp.title_pt || '';
    const body = sp[`body_${language}`] || sp.body_pt || '';
    const isClean = sp.template_key === 'clean_white';
    return (
      <div className={sp.click_url ? 'cursor-pointer' : ''} onClick={() => sp.click_url && onClick(sp)}
        style={{ background: st.bg, minHeight: '540px', position: 'relative', border: isClean ? '1px solid #e5e7eb' : 'none' }}
        data-testid={`spotlight-card-internal-${sp.id}`}>
        {isClean && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
            background: 'linear-gradient(90deg,transparent,#C9A962,transparent)' }} />
        )}
        <div className="flex flex-col items-center justify-center h-full text-center px-10 pt-16 pb-24">
          {isClean && (
            <div style={{ width: 60, height: 1, background: '#C9A962', margin: '0 auto 22px' }} />
          )}
          <h2 className={`mb-4 ${isClean ? 'text-4xl md:text-5xl' : 'text-3xl md:text-5xl'} font-bold`}
              style={{ color: st.color, fontFamily: 'Fraunces, Georgia, serif',
                       letterSpacing: isClean ? '1px' : '0', fontWeight: isClean ? 500 : 700 }}>
            {title}
          </h2>
          {isClean && (
            <div style={{ width: 60, height: 1, background: '#C9A962', margin: '0 auto 22px' }} />
          )}
          <p className={`${isClean ? 'text-base md:text-lg text-slate-600' : 'text-lg md:text-xl'} max-w-2xl whitespace-pre-line`}
             style={isClean ? { fontFamily: 'Georgia, serif', lineHeight: 1.7 } : { color: st.color }}>
            {body}
          </p>
          {sp.click_url && (
            <div className={`mt-8 inline-block rounded-full font-semibold shadow-lg
              ${isClean ? 'px-8 py-3 text-white border' : 'px-6 py-2 text-white'}`}
              style={isClean
                ? { background: '#111A2C', borderColor: '#C9A962', fontFamily: 'Georgia, serif', letterSpacing: '.5px' }
                : { background: st.accent }}>
              {language === 'pt' ? 'Saiba mais' : language === 'en' ? 'Learn more' : 'Saber más'} →
            </div>
          )}
        </div>
        <img src="/logo-demarre.png" alt="Demarré Tours"
          className={`absolute ${isClean ? 'top-6' : 'bottom-4'} left-1/2 -translate-x-1/2 h-8 ${isClean ? '' : 'opacity-80'}`}
          style={{ pointerEvents: 'none' }}/>
        {isClean && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
            background: 'linear-gradient(90deg,transparent,#C9A962,transparent)' }} />
        )}
      </div>
    );
  }
  return null;
}

export default HomeSpotlight;

// Exported for the admin Preview modal in SpotlightsManager. Uses the exact
// same renderer used publicly, so what admins see is what pilgrims will see.
export { renderCard as renderSpotlightCard, TEMPLATE_STYLES, tagLabels as spotlightTagLabels, tagColors as spotlightTagColors };
